import type { Invoice } from '../../api/types.js';
import type { State } from '../../api/invoices.js';
import { describeOutcome } from './disputeOutcome.js';

// REQ-FE-033 area A4: labels match mockup `03-invoice-list.png` —
// uppercase visually, FR copy, "MISE À DISPOSITION" for AVAILABLE
// (mockup shows it for the "available to acknowledge" state), and
// "EN LITIGE" / "REJETÉE" for the dispute/reject states. CSS keeps
// the pill compact so the longer FR strings still fit on one line.
const STATE_LABEL_FR: Record<State, string> = {
  RECEIVED: 'REÇUE',
  PROCESSING: 'EN TRAITEMENT',
  AVAILABLE: 'MISE À DISPOSITION',
  ACKNOWLEDGED: 'ACCUSÉE',
  REJECTED: 'REJETÉE',
  VOIDED: 'ANNULÉE',
  SEND_PENDING: 'ENVOI EN COURS',
  SEND_UNCONFIRMED: 'ENVOI NON CONFIRMÉ',
  SEND_ACCEPTED: 'ENVOYÉE',
  SEND_REJECTED: 'ENVOI REFUSÉ',
  LEGACY_ONLY: 'HORS PLATEFORME',
};

const STATE_TONE: Record<State, string> = {
  RECEIVED: 'neutral',
  PROCESSING: 'info',
  AVAILABLE: 'success',
  ACKNOWLEDGED: 'success',
  REJECTED: 'alert',
  VOIDED: 'neutral',
  SEND_PENDING: 'info',
  SEND_UNCONFIRMED: 'warning',
  SEND_ACCEPTED: 'success',
  SEND_REJECTED: 'alert',
  LEGACY_ONLY: 'neutral',
};

interface Props {
  state: Invoice['state'];
  dispute?: Invoice['dispute'];
}

/**
 * State badge. When `dispute != null`, surfaces the synthetic "Disputed"
 * label regardless of the underlying state — the v0.10.0 contract still
 * carries the dispute as a separate field rather than a new state enum
 * value (per packages/api-contract/openapi.yaml).
 */
export function StateBadge({ state, dispute }: Props) {
  if (dispute) {
    if (dispute.resolution) {
      const o = describeOutcome(dispute.resolution.outcome);
      return (
        <span
          className={`pill ${o.pillClass}`}
          data-testid="state-badge"
          data-state="DISPUTED"
          data-outcome={dispute.resolution.outcome}
        >
          {o.labelFr}
        </span>
      );
    }
    return (
      <span className="pill pill--warning" data-testid="state-badge" data-state="DISPUTED">
        EN LITIGE
      </span>
    );
  }
  return (
    <span
      className={`pill pill--${STATE_TONE[state]}`}
      data-testid="state-badge"
      data-state={state}
    >
      {STATE_LABEL_FR[state]}
    </span>
  );
}
